import { Plan, PricingVariant } from '@wix/ambassador-pricing-plans-v3-plan/types';
import { VariantIdsByPlan, VariantVisibilityState } from '../services/variant-visibility-storage';
import { isTruthy } from './isTruthy';
import { VariantCategoryNameById } from './variant-categories';

export type VariantsByPlan = Record<NonNullable<Plan['id']>, PricingVariant[]>;

export function getServerVisibleVariants(plan: Plan): PricingVariant[] {
  return (plan.pricingVariants ?? []).filter((variant) => variant.visible !== false);
}

export function getVisibleVariantIds(plans: Plan[], state: VariantVisibilityState): Set<string> {
  if (state.isVisibleVariantsMode) {
    return _getVisibleIdsFromWhitelist(plans, state.visibleVariantsByPlan);
  }
  return _getVisibleIdsFromBlacklist(plans, state.hiddenVariantsByPlan);
}

export function getCategorizedVisibleVariants(
  plan: Plan,
  variantCategoryNameById: VariantCategoryNameById,
): PricingVariant[] {
  return getServerVisibleVariants(plan).filter((variant) =>
    variant.categoryIds?.some((categoryId) => variantCategoryNameById[categoryId]),
  );
}

/**
 * Classic view gives a variant its own card and row only when it has a category.
 * A plan whose variants have none is one entry, represented by its first variant.
 */
export function getSelectableVariants(plan: Plan, variantCategoryNameById: VariantCategoryNameById): PricingVariant[] {
  const categorizedVariants = getCategorizedVisibleVariants(plan, variantCategoryNameById);

  return categorizedVariants.length ? categorizedVariants : getServerVisibleVariants(plan).slice(0, 1);
}

export function getVisibleVariantsByPlan({
  orderedPlans,
  variantVisibilityState,
  variantCategoryNameById,
}: {
  orderedPlans: Plan[];
  variantVisibilityState: VariantVisibilityState;
  variantCategoryNameById: VariantCategoryNameById;
}): VariantsByPlan {
  return Object.fromEntries(
    orderedPlans.flatMap((plan) => {
      const selectableVariants = getSelectableVariants(plan, variantCategoryNameById);
      const whitelistForPlan = variantVisibilityState.visibleVariantsByPlan[plan.id!] ?? [];
      const blacklistForPlan = variantVisibilityState.hiddenVariantsByPlan[plan.id!] ?? [];

      const filteredVariants = variantVisibilityState.isVisibleVariantsMode
        ? selectableVariants.filter((variant) => whitelistForPlan.includes(variant.id!))
        : selectableVariants.filter((variant) => !blacklistForPlan.includes(variant.id!));

      const variantOrder = variantVisibilityState.variantOrderByPlan[plan.id!];
      const visibleVariants = variantOrder?.length
        ? _sortByExplicitOrder(filteredVariants, variantOrder)
        : filteredVariants;

      return visibleVariants.length ? [[plan.id!, visibleVariants] as const] : [];
    }),
  );
}

function _sortByExplicitOrder(variants: PricingVariant[], orderedIds: string[]): PricingVariant[] {
  const variantById = new Map(variants.map((variant) => [variant.id!, variant]));
  const ordered = orderedIds.map((id) => variantById.get(id)).filter(isTruthy);
  const unordered = variants.filter((variant) => !orderedIds.includes(variant.id!));

  return [...ordered, ...unordered];
}

function _getVisibleIdsFromWhitelist(plans: Plan[], visibleVariantsByPlan: VariantIdsByPlan): Set<string> {
  const visibleIds = plans.flatMap((plan) => {
    const allowed = visibleVariantsByPlan[plan.id!] ?? [];

    return getServerVisibleVariants(plan)
      .map((variant) => variant.id)
      .filter(isTruthy)
      .filter((variantId) => allowed.includes(variantId));
  });

  return new Set(visibleIds);
}

function _getVisibleIdsFromBlacklist(plans: Plan[], hiddenVariantsByPlan: VariantIdsByPlan): Set<string> {
  const visibleIds = plans.flatMap((plan) => {
    const hidden = hiddenVariantsByPlan[plan.id!] ?? [];

    return getServerVisibleVariants(plan)
      .map((variant) => variant.id)
      .filter(isTruthy)
      .filter((variantId) => !hidden.includes(variantId));
  });
  return new Set(visibleIds);
}
